import { initializeApp } from 'firebase/app'
import {
  getAuth,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  onAuthStateChanged as firebaseOnAuthStateChanged
} from 'firebase/auth'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebasestorage.app`,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

const app = initializeApp(firebaseConfig)
const auth = getAuth(app)

export const FirebaseService = {
  /**
   * Login con popup de Google. Devuelve { idToken, email, displayName, uid }.
   */
  async loginWithGoogle () {
    const provider = new GoogleAuthProvider()
    const result = await signInWithPopup(auth, provider)
    const idToken = await result.user.getIdToken()
    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      uid: result.user.uid
    }
  },

  /**
   * Login con popup de Facebook. Devuelve { idToken, email, displayName, uid }.
   */
  async loginWithFacebook () {
    const provider = new FacebookAuthProvider()
    const result = await signInWithPopup(auth, provider)
    const idToken = await result.user.getIdToken()
    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      uid: result.user.uid
    }
  },

  /**
   * Login con popup de Apple. Devuelve { idToken, email, displayName, uid }.
   */
  async loginWithApple () {
    const provider = new OAuthProvider('apple.com')
    const result = await signInWithPopup(auth, provider)
    const idToken = await result.user.getIdToken()
    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      uid: result.user.uid
    }
  },

  /**
   * Re-autentica al usuario actual con su contraseña actual.
   * Usar antes de operaciones sensibles (cambio de contraseña).
   * Lanza FirebaseError si la contraseña es incorrecta (auth/invalid-credential).
   */
  async reauthenticateWithPassword (password) {
    if (!auth.currentUser?.email) throw new Error('No hay usuario autenticado con email/password')
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password)
    return reauthenticateWithCredential(auth.currentUser, credential)
  },

  /**
   * Login con email y contraseña via Firebase. Devuelve el idToken.
   */
  async loginWithEmailPassword (email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password)
    return result.user.getIdToken()
  },

  /**
   * Crea un usuario en Firebase con email y contraseña. Devuelve el uid.
   */
  async createUser (email, password) {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    return result.user.uid
  },

  /**
   * Devuelve el idToken del usuario actualmente autenticado en Firebase.
   * Espera a que Firebase restaure la sesión si currentUser aún no está disponible.
   * Lanza si no hay usuario activo.
   */
  async getIdToken () {
    if (auth.currentUser) return auth.currentUser.getIdToken()
    return new Promise((resolve, reject) => {
      const unsubscribe = firebaseOnAuthStateChanged(auth, user => {
        unsubscribe()
        if (user) resolve(user.getIdToken())
        else reject(new Error('No hay usuario autenticado en Firebase'))
      })
    })
  },

  /**
   * Cierra la sesión en Firebase.
   */
  async signOut () {
    return signOut(auth)
  },

  /**
   * Devuelve true si el usuario actual tiene auth de email/contraseña vinculada.
   * Útil para mostrar/ocultar la opción "Cambiar Contraseña".
   */
  hasPasswordProvider () {
    return auth.currentUser?.providerData.some(p => p.providerId === 'password') ?? false
  },

  /**
   * Registra un callback para cambios de estado de autenticación.
   */
  onAuthStateChanged (callback) {
    return firebaseOnAuthStateChanged(auth, callback)
  }
}
